/**
 * Shared health aggregation for an agency row (one or more advertisers).
 * Single advertiser: that score. Multiple: rounded average when every advertiser
 * has finished loading and has a score (same rules as health summary cards).
 */
export const getAgencyHealthFromAdvertisers = (advertisers) => {
  const advs = advertisers || [];
  if (advs.length === 0) {
    return { healthSummaryScore: null, isHealthSummaryLoading: true };
  }

  if (advs.length === 1) {
    const a = advs[0].advertiser;
    const isHealthSummaryLoading = a.isLoading || a.healthScore === null;
    return {
      healthSummaryScore: isHealthSummaryLoading ? null : a.healthScore,
      isHealthSummaryLoading,
    };
  }

  let sum = 0;
  for (const { advertiser: a } of advs) {
    if (a.isLoading || a.healthScore === null) {
      return { healthSummaryScore: null, isHealthSummaryLoading: true };
    }
    sum += a.healthScore;
  }

  return {
    healthSummaryScore: Math.round(sum / advs.length),
    isHealthSummaryLoading: false,
  };
};

/**
 * Reorders only the `advertisers` array inside a group (lowest health first).
 * Does not change order of agencies. Pending / no score rows sort last, tie-break by id.
 */
export const sortAdvertisersWithinGroupByHealth = (advertisers) => {
  return [...(advertisers || [])].sort((x, y) => {
    const xScore =
      !x?.advertiser ||
      x.advertiser.isLoading ||
      x.advertiser.healthScore === null
        ? null
        : x.advertiser.healthScore;
    const yScore =
      !y?.advertiser ||
      y.advertiser.isLoading ||
      y.advertiser.healthScore === null
        ? null
        : y.advertiser.healthScore;

    if (xScore === null && yScore === null) {
      return String(x.id).localeCompare(String(y.id));
    }
    if (xScore === null) return 1;
    if (yScore === null) return -1;
    if (xScore !== yScore) return xScore - yScore;
    return String(x.id).localeCompare(String(y.id));
  });
};

export const mapAgencyGroupsWithSortedAdvertisers = (groups) =>
  (groups || []).map((g) => ({
    ...g,
    advertisers: sortAdvertisersWithinGroupByHealth(g.advertisers),
  }));

const getAgencyGroupTieLabel = (group) => {
  const first = group.advertisers?.[0]?.advertiser;
  return first?.businessName || String(group.agencyID ?? "");
};

/**
 * Sorts agency groups by aggregate health (lowest score first / ascending).
 * Groups still loading or without a score sort last, then by business name.
 * Advertisers within each group are sorted by business name.
 */
export const sortAgencyGroupsByHealth = (agencyGroups) => {
  if (!Array.isArray(agencyGroups) || agencyGroups.length === 0) {
    return [];
  }

  const withSortedAdvertisers = agencyGroups.map((g) => ({
    ...g,
    advertisers: [...(g.advertisers || [])].sort((x, y) =>
      String(x.advertiser?.businessName || "").localeCompare(
        String(y.advertiser?.businessName || ""),
        undefined,
        { sensitivity: "base" }
      )
    ),
  }));

  return [...withSortedAdvertisers].sort((a, b) => {
    const metaA = getAgencyHealthFromAdvertisers(a.advertisers);
    const metaB = getAgencyHealthFromAdvertisers(b.advertisers);
    const pendingA =
      metaA.isHealthSummaryLoading || metaA.healthSummaryScore === null;
    const pendingB =
      metaB.isHealthSummaryLoading || metaB.healthSummaryScore === null;

    if (pendingA !== pendingB) {
      return pendingA ? 1 : -1;
    }
    if (!pendingA && !pendingB) {
      const diff = metaA.healthSummaryScore - metaB.healthSummaryScore;
      if (diff !== 0) return diff;
    }
    return getAgencyGroupTieLabel(a).localeCompare(
      getAgencyGroupTieLabel(b),
      undefined,
      { sensitivity: "base" }
    );
  });
};
